import { useSelector } from 'react-redux';
import { RootState } from '../store';

/**
 * useAccountType — capability hook for the whole app.
 *
 * Single source of truth for what a user can do. Components should never
 * branch on `user.accountType` directly; read a flag here instead. New
 * capabilities are added in this file once and consumed everywhere.
 *
 * Sources, in priority order:
 *   1. Backend-driven `user.features` overrides (subscription / experiment /
 *      kill-switch flags returned by `/users/me`). When the backend says a
 *      capability is OFF, it wins.
 *   2. Account-type defaults derived from `user.accountType`.
 *
 * Account types:
 *  - creator    : Full access. Can post, sell, message, use creator tools.
 *  - individual : Default browsing tier. Can post archive content, follow, buy.
 *  - buyer      : Shopping + custom-request tier. Can browse, save items,
 *                 buy products, and start DM threads with creators (where
 *                 custom-made requests happen — *not* the orders page).
 *                 Cannot post archive content, open a store, or use creator
 *                 tools.
 */

export interface CapabilityFlags {
  // Creator-only surfaces
  canPostArchive: boolean;
  canOpenStore: boolean;
  canUseCreatorsStudio: boolean;
  canAccessInsights: boolean;
  canAccessVerification: boolean;
  canAccessPromotion: boolean;
  canHavePublicProfile: boolean;
  canBeMentioned: boolean;
  // Buyer-facing surfaces
  canBuyProducts: boolean;
  /** Custom-made dress requests are conversational — they happen inside the
   *  designer's chat panel, not as a separate "create order" form. This flag
   *  controls whether the buyer is offered the entry-point. */
  canRequestCustom: boolean;
  canSaveFavorites: boolean;
  canViewOrders: boolean;
  // Messaging
  canInitiateDM: boolean;
  canReceiveDMFromAnyone: boolean;
}

export const useAccountType = () => {
  const user = useSelector((s: RootState) => s.auth.user);
  const accountType = user?.accountType;

  const isCreator = accountType === 'creator';
  const isIndividual = accountType === 'individual';
  const isBuyer = accountType === 'buyer';
  const isAuthed = !!accountType;

  // Defaults derived from account type. Backend-driven `user.features`
  // overrides are merged below so the server can flip any single flag off
  // (e.g. a buyer experiment, a paused checkout, etc.) without a deploy.
  const defaults: CapabilityFlags = {
    canPostArchive: !isBuyer,
    canOpenStore: isCreator,
    canUseCreatorsStudio: !isBuyer,
    canAccessInsights: !isBuyer,
    canAccessVerification: !isBuyer,
    canAccessPromotion: !isBuyer,
    canHavePublicProfile: !isBuyer,
    canBeMentioned: !isBuyer,

    canBuyProducts: isAuthed,
    canRequestCustom: isAuthed,
    canSaveFavorites: isAuthed,
    canViewOrders: isBuyer,

    canInitiateDM: isAuthed,
    canReceiveDMFromAnyone: !isBuyer,
  };

  // Merge backend overrides. `user.features` is sparse; only `false` values
  // turn a default OFF. Backend can never grant a capability the account
  // type doesn't already allow — that's an authorization bug, not a flag.
  const overrides = (user as any)?.features as Partial<CapabilityFlags> | undefined;
  const flags: CapabilityFlags = { ...defaults };
  if (overrides) {
    (Object.keys(defaults) as (keyof CapabilityFlags)[]).forEach((key) => {
      if (overrides[key] === false) flags[key] = false;
    });
  }

  return {
    accountType,
    isCreator,
    isIndividual,
    isBuyer,
    flags,

    // Back-compat: keep top-level boolean exports so existing call sites
    // don't have to change in one go. New code should read from `flags`.
    ...flags,
  };
};
